import { useEffect, useState, type FormEvent } from 'react';
import { FileText, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTemplates, useTemplateContent, useCreateFile } from '@/hooks/useObsidianWrite';
import { cn } from '@/lib/utils';

interface NewDocModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (path: string) => void;
}

export function NewDocModal({ open, onOpenChange, onCreated }: NewDocModalProps): JSX.Element {
  const { data: templatesData } = useTemplates();
  const create = useCreateFile();

  const [nome, setNome] = useState('');
  const [folder, setFolder] = useState('');
  const [templatePath, setTemplatePath] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: templateContent } = useTemplateContent(templatePath);

  useEffect(() => {
    if (open && templatesData?.default_folder && !folder) {
      setFolder(templatesData.default_folder);
    }
    if (!open) {
      setNome('');
      setTemplatePath(null);
      setError(null);
    }
  }, [open, templatesData, folder]);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!nome.trim()) return;

    const safeName = nome.trim().replace(/\.md$/i, '');
    const path = folder.trim()
      ? `${folder.replace(/\/$/, '')}/${safeName}.md`
      : `${safeName}.md`;

    const content = templateContent?.content ?? `# ${safeName}\n\n`;

    try {
      await create.mutateAsync({ path, content });
      onCreated(path);
      onOpenChange(false);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const templates = templatesData?.templates ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" /> Novo doc
            </DialogTitle>
            <DialogDescription>
              Cria um arquivo markdown no vault. Default: {templatesData?.default_folder ?? '09 - Central'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 my-4">
            <div className="grid grid-cols-[1fr_2fr] gap-2">
              <div>
                <label htmlFor="folder" className="block text-sm font-medium mb-1">
                  Pasta
                </label>
                <Input
                  id="folder"
                  value={folder}
                  onChange={(e) => setFolder(e.target.value)}
                  placeholder="09 - Central"
                />
              </div>
              <div>
                <label htmlFor="nome" className="block text-sm font-medium mb-1">
                  Nome (sem .md)
                </label>
                <Input
                  id="nome"
                  required
                  autoFocus
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Minha nova nota"
                />
              </div>
            </div>

            {templates.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                  <Sparkles className="h-3.5 w-3.5" /> Template (opcional)
                </label>
                <div className="grid grid-cols-2 gap-1.5 max-h-[180px] overflow-y-auto p-1">
                  <button
                    type="button"
                    onClick={() => setTemplatePath(null)}
                    className={cn(
                      'text-left text-xs p-2 rounded border',
                      templatePath === null ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent',
                    )}
                  >
                    <div className="font-medium">Em branco</div>
                    <div className="text-muted-foreground text-[10px]">Só título do doc</div>
                  </button>
                  {templates.map((t) => (
                    <button
                      key={t.path}
                      type="button"
                      onClick={() => setTemplatePath(t.path)}
                      className={cn(
                        'text-left text-xs p-2 rounded border',
                        templatePath === t.path
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-accent',
                      )}
                    >
                      <div className="font-medium truncate">{t.name}</div>
                      <div className="text-muted-foreground text-[10px]">
                        {t.source === 'vault' ? '📚 Vault' : '⭐ Central'} · {(t.size / 1024).toFixed(1)}KB
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={create.isPending || !nome.trim()}>
              {create.isPending ? 'Criando…' : 'Criar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
